import { DEFAULT_TOKEN_PATH, MonitorHubConnection } from "./monitor-hub.ts";
import type { MonitorHubOptions } from "./hub/monitor-hub-options.ts";
import {
  createInitialLineMonitorState,
  reduceLineMonitorState,
  rowsWithActivityOrOneFallback,
  selectLineViewRows,
  type ActiveCallDetail,
  type LineTrafficLight,
  type LineViewRow,
} from "./line-monitor-state.ts";
import { attachShadowCss } from "./styles";

export interface AircallMonitorCreateOptions {
  /** CSS selector or element where the shadow root attaches */
  container: HTMLElement | string;
  /** Same URL passed to SignalR `.withUrl` (typically your hub endpoint). */
  hubUrl: string;
  hubOptions?: Partial<MonitorHubOptions>;
  /** Ruta HTTP del endpoint de token; por defecto el valor en `DEFAULT_TOKEN_PATH`. */
  tokenPath?: string;
  /**
   * Identificador numérico del agente en Aircall (opcional). Reservado para futura UX
   * “tú en esta llamada”; el semáforo actual no lo requiere.
   */
  myUserId?: number;
  /** Si es true, se añade un registro textual de cada evento al final del widget. */
  debugLog?: boolean;
}

export interface AircallMonitorInstance {
  destroy(): Promise<void>;
  /** Contenedor principal del listado de líneas (Shadow DOM). */
  getHubEvents(): HTMLElement | null;
}

function resolveRoot(container: HTMLElement | string): HTMLElement {
  if (typeof container === "string") {
    const el = document.querySelector(container);
    if (!el || !(el instanceof HTMLElement)) {
      throw new Error(`AircallMonitor: elemento no encontrado (${container}).`);
    }
    return el;
  }
  return container;
}

function lightClass(light: LineTrafficLight): string {
  switch (light) {
    case "red":
      return "am-line--red";
    case "yellow":
      return "am-line--yellow";
    case "blue":
      return "am-line--blue";
    default:
      return "am-line--green";
  }
}

function lightTitleEs(light: LineTrafficLight): string {
  switch (light) {
    case "red":
      return "Peor estado: entrante sin agente libre";
    case "yellow":
      return "Peor estado: entrante en espera";
    case "blue":
      return "Hay llamada en curso (resumen de línea)";
    default:
      return "Sin llamadas activas en esta línea";
  }
}

/** Tooltip por fila de sesión (no usar el texto «peor estado» de la tarjeta). */
function callRowLightTitleEs(light: LineTrafficLight): string {
  switch (light) {
    case "red":
      return "Sin agente libre en esta línea";
    case "yellow":
      return "Timbre o espera (sin contestar)";
    case "blue":
      return "Contestada / en curso";
    default:
      return "Sesión";
  }
}

/** Fecha/hora en hora local: DD/MM/YYYY, HH:mm (siempre ceros a la izquierda). */
function formatEventTime(ms: number): string {
  const d = new Date(ms);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year}, ${hh}:${mm}`;
}

/** Duración desde `fromMs` hasta `now` (m:ss o h:mm:ss). */
function formatElapsedMs(fromMs: number, now: number = Date.now()): string {
  const sec = Math.max(0, Math.floor((now - fromMs) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

function createActiveCallRowEl(
  d: ActiveCallDetail,
  rowClass: string,
): HTMLElement {
  const row = document.createElement("div");
  row.className = `${rowClass} am-line-call-row--sess-${d.rowLight}`;
  row.title = callRowLightTitleEs(d.rowLight);

  const time = document.createElement("time");
  time.className = "am-call-time";
  time.dateTime = new Date(d.atMs).toISOString();
  time.textContent = formatEventTime(d.atMs);

  const dur = document.createElement("span");
  dur.className = "am-call-duration";
  dur.setAttribute("data-am-call-start", String(d.atMs));
  dur.title = d.isAnswered
    ? "Duración de la llamada (desde la contestación)"
    : "Tiempo desde la creación de la sesión (hasta que contesten)";
  dur.textContent = formatElapsedMs(d.atMs);

  const dir = document.createElement("span");
  const ddir = d.direction;
  dir.className = `am-call-dir am-call-dir--${ddir}`;
  if (ddir === "inbound") {
    dir.textContent = "<-";
    dir.setAttribute("aria-label", "Entrante");
    dir.title = "Entrante";
  } else if (ddir === "outbound") {
    dir.textContent = "->";
    dir.setAttribute("aria-label", "Saliente");
    dir.title = "Saliente";
  } else {
    dir.textContent = "?";
    dir.setAttribute("aria-label", "Dirección desconocida");
    dir.title = "Dirección desconocida";
  }

  const num = document.createElement("span");
  num.className = "am-call-num";
  num.textContent = d.digits;

  row.append(time, dur, dir, num);

  if (d.userLabel) {
    const u = document.createElement("span");
    u.className = "am-call-user";
    u.textContent = d.userLabel;
    row.appendChild(u);
  }
  return row;
}

/** Chevron acordeón (plegado ⇒ abajo; abierto la clase --open rota 180°). */
function accordionChevronIcon(): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 16 16");
  svg.setAttribute("width", "12");
  svg.setAttribute("height", "12");
  svg.setAttribute("class", "am-accordion-chevron");
  svg.setAttribute("aria-hidden", "true");
  const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
  p.setAttribute("fill", "none");
  p.setAttribute("stroke", "currentColor");
  p.setAttribute("stroke-width", "1.6");
  p.setAttribute("stroke-linecap", "round");
  p.setAttribute("stroke-linejoin", "round");
  p.setAttribute("d", "M3.5 5.75 8 10.25 12.5 5.75");
  svg.appendChild(p);
  return svg;
}

/** Widget montado: DOM en ShadowRoot + `MonitorHubConnection` (SignalR). */
class AircallMonitorWidget implements AircallMonitorInstance {
  private readonly root: HTMLElement;
  private shadow: ShadowRoot;
  private readonly linesContainer: HTMLElement;
  private readonly alertBanner: HTMLElement;
  private readonly debugPanel: HTMLElement;
  private hub: MonitorHubConnection;
  private lineState = createInitialLineMonitorState();
  private showAllLines = false;
  private durationTicker: ReturnType<typeof setInterval> | null = null;

  private readonly onKeyDown = (ev: KeyboardEvent) => {
    if (ev.key === "Escape" && this.showAllLines) {
      this.showAllLines = false;
      this.render();
    }
  };

  constructor(options: AircallMonitorCreateOptions) {
    void options.myUserId;

    this.root = resolveRoot(options.container);

    if (typeof this.root.attachShadow !== "function") {
      throw new Error(
        "AircallMonitor: el navegador debe soportar attachShadow sobre el container.",
      );
    }

    this.shadow = this.root.attachShadow({ mode: "open" });

    this.alertBanner = document.createElement("div");
    this.alertBanner.className = "am-alert";
    this.alertBanner.setAttribute("role", "status");
    this.alertBanner.hidden = true;

    this.linesContainer = document.createElement("div");
    this.linesContainer.className = "am-lines";
    this.linesContainer.innerHTML =
      '<p class="am-placeholder">Esperando snapshot de líneas (UserNumbers)…</p>';

    this.debugPanel = document.createElement("div");
    this.debugPanel.className = "am-debug";
    this.debugPanel.hidden = !options.debugLog;

    const shell = document.createElement("div");
    shell.className = "am-shell";

    shell.append(this.alertBanner, this.linesContainer, this.debugPanel);

    attachShadowCss(this.shadow);
    this.shadow.appendChild(shell);

    const tokenPath =
      typeof options.tokenPath === "undefined"
        ? DEFAULT_TOKEN_PATH
        : options.tokenPath;

    const me = this;
    this.hub = new MonitorHubConnection(
      options.hubUrl,
      options.hubOptions,
      tokenPath,
      (hubEvent: string, user: unknown, item: unknown) => {
        me.lineState = reduceLineMonitorState(
          me.lineState,
          hubEvent,
          user,
          item,
        );
        me.render();
        if (options.debugLog) me.appendDebugLine(hubEvent);
      },
    );

    document.addEventListener("keydown", this.onKeyDown);
  }

  private createLineSectionShell(r: LineViewRow): HTMLElement {
    const lineEl = document.createElement("section");
    lineEl.className = `am-line ${lightClass(r.light)}`;
    lineEl.dataset.lineId = String(r.id);
    lineEl.setAttribute(
      "aria-label",
      `${r.name}, ${lightTitleEs(r.light)}, ${r.activeCallCount} sesiones`,
    );
    lineEl.title = `${r.name} — ${lightTitleEs(r.light)} · ${r.activeCallCount} sesión(es)`;
    return lineEl;
  }

  /** Fila título + badge + botón expandir (solo si `showListToggle`). */
  private appendLineMainRow(
    shell: HTMLElement,
    r: LineViewRow,
    showListToggle: boolean,
  ): void {
    const main = document.createElement("div");
    main.className = "am-line-main";

    const titleRow = document.createElement("div");
    titleRow.className = "am-line-title-row";
    const name = document.createElement("span");
    name.className = "am-line-name";
    name.textContent = r.name;
    const lineDigits = document.createElement("span");
    lineDigits.className = "am-line-digits";
    lineDigits.textContent = r.digits || "—";
    titleRow.append(name, lineDigits);

    const actions = document.createElement("div");
    actions.className = "am-line-actions";
    const badge = document.createElement("span");
    badge.className = "am-line-badge";
    badge.textContent = String(r.activeCallCount);
    badge.title =
      r.activeCallCount === 1
        ? "1 sesión asignada a esta línea"
        : `${r.activeCallCount} sesiones en esta línea (detalle abajo)`;
    actions.appendChild(badge);

    if (showListToggle) {
      const expandBtn = document.createElement("button");
      expandBtn.type = "button";
      expandBtn.className = `am-line-expand-btn${this.showAllLines ? " am-line-expand-btn--open" : ""}`;
      expandBtn.setAttribute("aria-expanded", String(this.showAllLines));
      expandBtn.title = this.showAllLines
        ? "Solo con actividad"
        : "Todas las líneas";
      expandBtn.setAttribute(
        "aria-label",
        this.showAllLines ? "Contraer listado" : "Ver todas las líneas",
      );
      expandBtn.appendChild(accordionChevronIcon());
      expandBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.showAllLines = !this.showAllLines;
        this.render();
      });
      actions.appendChild(expandBtn);
      shell.classList.add("am-line--accordion-head");
    }

    main.append(titleRow, actions);
    shell.appendChild(main);
  }

  private appendLineDetailsBlock(
    parent: HTMLElement,
    r: LineViewRow,
  ): void {
    if (r.activeCalls.length === 0) return;
    const details = document.createElement("div");
    details.className = "am-line-details";
    for (const ac of r.activeCalls) {
      details.appendChild(createActiveCallRowEl(ac, "am-line-call-row"));
    }
    parent.appendChild(details);
  }

  /** Sección completa (main + detalles): para el área con scroll, sin botón listado. */
  private buildFullLineSection(r: LineViewRow): HTMLElement {
    const shell = this.createLineSectionShell(r);
    this.appendLineMainRow(shell, r, false);
    this.appendLineDetailsBlock(shell, r);
    return shell;
  }

  /**
   * Continuación de la misma línea que la cabecera fija: detalles de llamadas
   * dentro del mismo borde/color de tarjeta (`.am-line--split-*`).
   */
  private buildPrimaryLineDetailsContinuation(r: LineViewRow): HTMLElement | null {
    if (r.activeCalls.length === 0) return null;
    const shell = this.createLineSectionShell(r);
    shell.classList.add("am-line--split-bottom");
    shell.setAttribute(
      "aria-label",
      `Llamadas activas — ${r.name}`,
    );
    this.appendLineDetailsBlock(shell, r);
    return shell;
  }

  /**
   * Solo la parte superior de la tarjeta (título + botón); si hay llamadas,
   * `buildPrimaryLineDetailsContinuation` va debajo dentro del mismo bloque no scroll (`am-lines-sticky-head`).
   */
  private buildAccordionHeadStrip(r: LineViewRow): HTMLElement {
    const shell = this.createLineSectionShell(r);
    if (r.activeCalls.length > 0) {
      shell.classList.add("am-line--split-top");
    }
    this.appendLineMainRow(shell, r, true);
    return shell;
  }

  getHubEvents(): HTMLElement | null {
    return this.linesContainer;
  }

  private updateCallDurationElements(): void {
    for (const el of this.shadow.querySelectorAll("[data-am-call-start]")) {
      if (!(el instanceof HTMLElement)) continue;
      const raw = el.getAttribute("data-am-call-start");
      const start = raw ? Number.parseInt(raw, 10) : NaN;
      if (!Number.isFinite(start)) continue;
      el.textContent = formatElapsedMs(start);
    }
  }

  /** Reloj 1s mientras haya filas con duración en el shadow. */
  private syncDurationTicker(): void {
    const n = this.shadow.querySelectorAll("[data-am-call-start]").length;
    if (n === 0) {
      if (this.durationTicker !== null) {
        clearInterval(this.durationTicker);
        this.durationTicker = null;
      }
      return;
    }
    this.updateCallDurationElements();
    if (this.durationTicker === null) {
      this.durationTicker = window.setInterval(() => {
        this.updateCallDurationElements();
      }, 1000);
    }
  }

  private render(): void {
    const ph = this.linesContainer.querySelector(".am-placeholder");
    const allRows = selectLineViewRows(this.lineState);
    if (allRows.length <= 1) {
      this.showAllLines = false;
    }
    const rows = this.showAllLines
      ? allRows
      : rowsWithActivityOrOneFallback(allRows);
    if (ph && allRows.length > 0) ph.remove();

    const hasRed = allRows.some((r) => r.light === "red");
    this.alertBanner.hidden = !hasRed;
    if (hasRed) {
      this.alertBanner.textContent =
        "Entrante sin agente libre.";
    }

    this.linesContainer.replaceChildren();

    if (allRows.length === 0 && !this.lineState.linesById.size) {
      const scrollWrap = document.createElement("div");
      scrollWrap.className = "am-lines-scroll";
      scrollWrap.innerHTML =
        '<p class="am-placeholder">Sin líneas en snapshot todavía.</p>';
      this.linesContainer.appendChild(scrollWrap);
      this.syncDurationTicker();
      return;
    }

    const scrollEl = document.createElement("div");
    scrollEl.className = "am-lines-scroll";

    const listToggle = allRows.length > 1 && rows.length > 0;

    if (listToggle) {
      const headWrap = document.createElement("div");
      headWrap.className = "am-lines-sticky-head";
      const primary = rows[0];
      const detailsPart = this.buildPrimaryLineDetailsContinuation(primary);

      headWrap.appendChild(this.buildAccordionHeadStrip(primary));
      if (detailsPart !== null) {
        headWrap.appendChild(detailsPart);
      }
      for (let i = 1; i < rows.length; i++) {
        scrollEl.appendChild(this.buildFullLineSection(rows[i]));
      }

      this.linesContainer.append(headWrap, scrollEl);
    } else {
      for (const r of rows) {
        scrollEl.appendChild(this.buildFullLineSection(r));
      }
      this.linesContainer.appendChild(scrollEl);
    }

    this.syncDurationTicker();
  }

  private appendDebugLine(hubEvent: string): void {
    const pre = document.createElement("pre");
    pre.className = "am-debug-line";
    pre.textContent = `[${new Date().toISOString()}] ${hubEvent}`;
    this.debugPanel.appendChild(pre);
    this.debugPanel.scrollTop = this.debugPanel.scrollHeight;
  }

  async destroy(): Promise<void> {
    if (this.durationTicker !== null) {
      clearInterval(this.durationTicker);
      this.durationTicker = null;
    }
    document.removeEventListener("keydown", this.onKeyDown);
    await this.hub.stop();
    this.shadow.replaceChildren();
  }
}

export function createWidget(
  options: AircallMonitorCreateOptions,
): AircallMonitorInstance {
  return new AircallMonitorWidget(options);
}
